import React from "react";
import { Container, Header, Segment } from "semantic-ui-react";

import HeroSectionComponent from "../../components/hero/HeroSection";
import BulkInquiryForm from "./BulkInquiryForm";
import "./WaterQualityView.css";

// HeroSection is a shared, untyped JS component. Describe just the props this page
// passes so we can consume it from TSX without loosening it to `any`.
interface HeroSectionProps {
  lightBg?: boolean;
  lightText?: boolean;
  lightTextDesc?: boolean;
  topLine?: string;
  headline?: React.ReactNode;
  description?: React.ReactNode;
  buttonLabel?: string;
  buttonLink?: string;
  buttonColor?: string;
  img?: string;
  alt?: string;
  imgStart?: string;
}
const HeroSection = HeroSectionComponent as React.FC<HeroSectionProps>;

/**
 * Public "Our Water" page (/water-quality).
 *
 * Copy is rendered verbatim from docs/water-quality/our-water-page-copy.md and the
 * layout follows docs/water-quality/our-water-page-mockup.html, built with the site's
 * own components: HeroSection for the hero and Semantic UI Segment/Container/Header
 * sections like components/hero/AboutSection1.js.
 *
 * The bulk-inquiry form (react-hook-form) renders in the sky "Inquire for Bulk Water"
 * band, below the content sections and above the contact prompt.
 */
const WaterQualityView: React.FC = () => {
  return (
    <div className="water-quality">
      <HeroSection
        lightBg={false}
        lightText={true}
        lightTextDesc={true}
        topLine="Our Water"
        headline={
          <>
            Pumped in Acton.
            <br />
            Hauled by the Lunde family.
          </>
        }
        description="Potable water for Acton, Agua Dulce, and the surrounding area — delivered by tanker, or filled at our yard."
        buttonLabel="Inquire for Bulk Water"
        buttonLink="#inquire"
        buttonColor="blue"
        img="/images/lunde-water-truck.jpg"
        alt="Lunde Water tanker truck"
      />

      {/* Where it comes from */}
      <Segment className="wq-section" style={{ padding: "5em 0em" }} vertical>
        <Container text>
          <p className="wq-kicker">Where it comes from</p>
          <Header as="h3" style={{ fontSize: "2em" }}>
            An Acton well, drilled into the valley floor
          </Header>
          <p>
            Lunde Water sits on about twenty acres of the family ranch at 3370
            Country Way, in Acton. The property was a horse operation before it
            was a water company.
          </p>
          <p>
            Our well runs about two hundred feet deep. The first ninety feet is
            white and grey sand and gravel — alluvium, the material a stream
            leaves behind over a very long time. Below that is the grey rock of
            the Vasquez Formation, which underlies this whole stretch of the
            valley. The well is eight-inch steel, sealed with cement through the
            top fifty feet so nothing from the surface can travel down the
            outside of the pipe, and screened below that into both the gravel and
            the rock. The water sits close to the surface here — it has stood
            about thirty feet down for as long as the family has owned the ranch.
          </p>
        </Container>
      </Segment>

      {/* Why Acton's water is its own */}
      <Segment className="wq-section wq-alt" style={{ padding: "5em 0em" }} vertical>
        <Container text>
          <p className="wq-kicker">Why Acton's water is its own</p>
          <Header as="h3" style={{ fontSize: "2em" }}>
            A small basin, on its own
          </Header>
          <p>
            Acton has its own small groundwater basin, on the upper Santa Clara
            River — a narrow band of sand, gravel, and old river terrace running
            along Soledad Canyon, boxed in by the hard bedrock of the surrounding
            mountains. It's a small, self-contained system, not a corner of a
            much larger one. It sits apart from the Antelope Valley to the north;
            when the Antelope Valley's groundwater was divided up in court, this
            basin was left out of it.
          </p>
          <p>
            Our well is near the south end of the basin, where the valley narrows
            and the water drains past on its way downstream. Because everything
            upstream flows toward this point, the level here holds steadier than
            it does higher up the basin, even in dry years.
          </p>
        </Container>
      </Segment>

      {/* Who we deliver to */}
      <Segment className="wq-section" style={{ padding: "5em 0em" }} vertical>
        <Container text>
          <p className="wq-kicker">Who we deliver to</p>
          <Header as="h3" style={{ fontSize: "2em" }}>
            The water supply for our neighbors
          </Header>
          <p>
            Most of our customers are households in and around Acton and Agua
            Dulce with no public water main and no reliable well of their own.
            For these neighbors, hauled water isn't a convenience — it's the
            water supply. Los Angeles County's own findings describe the
            operation as filling a gap in the community's water infrastructure.
            That's been the job for two decades.
          </p>
        </Container>
      </Segment>

      {/* Testing and oversight */}
      <Segment className="wq-section wq-alt" style={{ padding: "5em 0em" }} vertical>
        <Container text>
          <p className="wq-kicker">Testing &amp; oversight</p>
          <Header as="h3" style={{ fontSize: "2em" }}>
            Licensed and tested, every year
          </Header>
          <p>
            Water hauling in California is regulated by the Food and Drug Branch
            of the State Department of Public Health — the same branch that
            oversees bottled water. We hold that branch's annual licenses for
            both the water source and the water hauler:{" "}
            <strong>Private Water Source Operator License #86170</strong> and{" "}
            <strong>Water Hauler License #86170</strong>.
          </p>
          <p>
            Our water is tested every year by <strong>Eurofins</strong>, an
            ELAP-accredited laboratory, against California's Title 22
            drinking-water standards. In 2026 we also had it analyzed as a
            finished bottled product — a Title 21 analysis — confirming it still
            meets potability criteria after bottling.
          </p>
          <p>
            We don't publish results on this page. We'll send them to anyone with
            a reason to review them — the full Title 22 testing history and the
            Title 21 finished-product analysis. Just ask, using the form below.
          </p>
        </Container>
      </Segment>

      {/* Permitting */}
      <Segment className="wq-section" style={{ padding: "5em 0em" }} vertical>
        <Container text>
          <p className="wq-kicker">Permitting</p>
          <Header as="h3" style={{ fontSize: "2em" }}>
            Continuously permitted in Acton since 2005
          </Header>
          <p>
            The operation has been continuously permitted at this location since
            February 2005, currently under Los Angeles County Conditional Use
            Permit 201500022, approved May 6, 2020. In the review that led to
            that permit, the County Departments of Public Works, Fire, and Public
            Health each recommended approval with no required conditions, and the
            Acton Town Council wrote in support of the town's water haulers — this
            operation among them — as essential to the community.
          </p>
        </Container>
      </Segment>

      {/* What we can send you */}
      <Segment className="wq-section wq-alt" style={{ padding: "5em 0em" }} vertical>
        <Container text>
          <p className="wq-kicker">What we can send you</p>
          <Header as="h3" style={{ fontSize: "2em" }}>
            The documentation, on request
          </Header>
          <div className="wq-send-card">
            <p>Tell us what you need and we'll send it over:</p>
            <ul className="wq-send-list">
              <li>Title 22 drinking-water testing history</li>
              <li>Title 21 finished-product analysis</li>
            </ul>
          </div>
        </Container>
      </Segment>

      {/* Inquire for Bulk Water — the bulk-inquiry form (react-hook-form) renders in this band. */}
      <Segment
        id="inquire"
        className="wq-section wq-formband"
        style={{ padding: "5em 0em" }}
        vertical
      >
        <Container text>
          <Header as="h2" className="wq-formband-title" style={{ fontSize: "2.2em" }}>
            Inquire for Bulk Water
          </Header>
          <p className="wq-formband-lead">
            <strong>Looking for water in volume?</strong> Tell us a bit about
            what you need and we'll get back to you with the documentation and
            details.
          </p>

          <BulkInquiryForm />

          <p className="wq-altcontact">
            Prefer to talk? Call <a href="tel:+16615664426">(661) 566-4426</a> or
            email{" "}
            <a href="mailto:office@lundewaterllc.com">office@lundewaterllc.com</a>.
          </p>
        </Container>
      </Segment>

      {/* Disclaimer */}
      <div className="wq-disclaimer">
        Laboratory results and documentation are provided on request and reflect
        specific sampling events and the sources named in them. Contact us for
        current data.
      </div>
    </div>
  );
};

export default WaterQualityView;
